<template>
	<div>
		<v-data-table
			id="idListagemDocumentos"
			v-if="hasItens"
			v-model="documentosSelecionados"
			:show-select="isEditar && quantidadeDocumentosSelecionaveis > 0"
			:headers="colunas"
			:items="documentos"
			item-key="id"
			:key="atualizarListagem"
			hide-default-footer
			:server-items-length="totalItens"
			:options="paginacaoInterna"
			no-data-text="Nenhum documento por aqui"
			@click:row="visualizarDocumentoAnexo"
			class="arp-listview row-pointer"
		>
			<template #[`header.data-table-select`]="{}">
				<v-simple-checkbox
					id="caixaDeSelecao"
					:value="todosItensEstaoSelecionados"
					color="primary"
					@input="onSelectAll($event)"
					@click.stop
				></v-simple-checkbox>
			</template>

			<template #[`item.data-table-select`]="{ isSelected, select, item }">
				<v-simple-checkbox
					v-if="itemPodeSelecionar(item)"
					id="caixaDeSelecao"
					class="row-checkbox"
					color="primary"
					:value="isSelected"
					@input="select($event)"
					@click.stop
				></v-simple-checkbox>
			</template>

			<template #[`item.tipoRegistro`]="{ item }">
				<v-row justify="center">
					<v-tooltip top>
						<template #activator="{ on, attrs }">
							<v-icon v-bind="attrs" v-on="on" @click.prevent.stop
								>{{
									estaDesatualizadoOuJuntadoDesatualizado(item)
										? 'mdi-paperclip-remove'
										: tipoDocumentoRegistro[item.tipoRegistroDocumento].icon
								}}
							</v-icon>
						</template>
						<span>{{
							estaDesatualizadoOuJuntadoDesatualizado(item)
								? 'Desatualizado - Foi gerada uma nova versão do documento'
								: tipoDocumentoRegistro[item.tipoRegistroDocumento].text
						}}</span>
					</v-tooltip>
				</v-row>
			</template>

			<template #[`item.signatarios`]="{ item }">
				<v-menu
					v-if="ehDocumentoProcessoDigital(item)"
					id="iconeSignatariosConvidados"
					open-on-hover
					top
					left
					offset-x
					:close-on-content-click="false"
				>
					<template #activator="{ on, attrs }">
						<div v-bind="attrs" v-on="on" @click.stop>
							<v-icon size="18" class="mb-1 mr-4">mdi-account-outline</v-icon>
							<span>{{ getQuantidadeAssinadosDocumento(item) }}</span>
						</div>
					</template>

					<v-list v-if="!ehListaSignatariosVazia(item)" id="listaSignatarios">
						<v-list-item-group>
							<v-list-item v-for="signatario in getListaSignatarios(item)" :key="signatario.email">
								<v-list-item-icon>
									<v-icon size="18" :color="getCorIconeSituacaoSignatario(signatario.situacao)">
										{{ getIconeSituacaoSignatario(signatario.situacao) }}
									</v-icon>
								</v-list-item-icon>
								<v-list-item-content>
									<v-list-item-title>
										<texto-tooltip :texto="signatario.email" :tamanho-maximo="30"></texto-tooltip>
									</v-list-item-title>
								</v-list-item-content>
							</v-list-item>
							<v-list-item v-if="item.signatarios.itens.length > 5">
								<v-list-item-content>
									<v-list-item-title>
										<v-btn
											text
											block
											@click="tratarEventoVisualizarSignatarios(item.id)"
											color="primary"
											>Ver Todos
										</v-btn>
									</v-list-item-title>
								</v-list-item-content>
							</v-list-item>
						</v-list-item-group>
					</v-list>
				</v-menu>
			</template>

			<template #[`item.titulo`]="{ item }">
				<v-tooltip :disabled="!item.tipoDocumento" max-width="800" top color="black">
					<template #activator="{ on, attrs }">
						<span v-bind="attrs" v-on="on" :class="getClasseLinhasPorSituacao(item)">{{
							getTituloDocumento(item)
						}}</span>
						<span>{{ item.nomeTipoDocumento }}</span>
					</template>
					<span>{{ 'Tipo: ' + item.tipoDocumento }}</span>
				</v-tooltip>
			</template>

			<template #[`item.dataCadastro`]="{ item }">
				<texto-tooltip
					:class="getClasseLinhasPorSituacao(item)"
					:texto="formatDataFilter(item.dataCadastro)"
					:texto-tooltip="`Autor: ${item.autor}`"
				></texto-tooltip>
			</template>

			<template #item.situacao="{ item }">
				<span :class="getClasseLinhasPorSituacao(item)"> {{ estaJuntado(item) }}</span>
			</template>

			<template #[`item.acoes`]="{ item }" v-if="isEditar">
				<div class="d-flex flex-row align-center align-content-center px-0">
					<div class="d-flex align-center ml-auto">
						<v-tooltip v-if="exibirIconeSituacaoEDocs(item)" top>
							<template #activator="{ on }">
								<v-icon :color="colorsSituacaoEDocs[item.situacaoEdocs]" v-on="on">
									{{ iconesSituacaoEDocs[item.situacaoEdocs] }}
								</v-icon>
							</template>
							<span>{{ mensagensSituacaoEDocs[item.situacaoEdocs] }}</span>
						</v-tooltip>
						<botao-tooltip-datatable
							v-if="podeExibirAcaoAssinar(item)"
							icone="mdi-draw-pen"
							cor="grey"
							v-az-auth="montarPermissoes(permissaoAssinarEletronicamente, permissaoAssinarDigitalmente)"
							texto="Assinar"
							@click="abrirModalSelecionarTipoAssinatura(item)"
						></botao-tooltip-datatable>
						<botao-tooltip-datatable
							icone="mdi-file-edit"
							cor="grey"
							texto="Redigir"
							v-az-auth="permissaoRedigir"
							@click="redigirDocumento(item)"
							v-if="podeExibirAcaoRedigir(item)"
						></botao-tooltip-datatable>
						<botao-tooltip-datatable
							v-if="deveExibirBotaoJuntar(item)"
							icone="mdi-file-replace"
							texto="Juntar"
							v-az-auth="permissaoJuntar"
							cor="grey"
							@click="abrirOuFecharModalJuntarDocumento(item)"
						></botao-tooltip-datatable>
					</div>
					<div>
						<listagem-documentos-btn-mais-acoes
							:documento-ou-anexo="item"
							:has-authority="temPermissao"
							:exibir-acoes-pncp="exibirAcoesPncp"
							@baixar="baixarDocumentoAnexo(item)"
							@excluir="abrirOuFecharModalExclusaoDocumentoAnexo(item)"
							@cancelar="abrirModalInformarMotivoCancelamento(item)"
							@concluir-edicao-documento="concluirEdicaoDocumento(item)"
							:permissao-concluir-edicao-documento="permissaoConcluirEdicaoDocumento"
							:permissao-excluir-documento="permissaoExcluirDocumento"
							:permissao-baixar-documento="permissaoBaixarDocumento"
							:permissao-convidar-signatarios="permissaoConvidarSignatarios"
							:permissao-cancelar-documento="permissaoCancelarDocumento"
							:permissao-desentranhar-documento="permissaoDesentranharDocumento"
							:permissao-pncp-enviar="permissaoPncpEnviar"
							:permissao-pncp-excluir="permissaoPncpExcluir"
							@tratar-evento-desentranhar="tratarEventoDesentranhar(item)"
							@enviar-pncp="abrirOuFecharModalEnviarPncp(item)"
							@excluir-pncp="abrirOuFecharModalExcluirPncp(item)"
							@convidar-signatarios="tratarEventoConvidarSignatarios(item)"
						></listagem-documentos-btn-mais-acoes>
					</div>
				</div>
			</template>
		</v-data-table>

		<paginacao-tabela
			v-if="hasItens"
			:page.sync="paginacaoInterna.page"
			:rows-per-page.sync="paginacaoInterna.rowsPerPage"
			:totalPaginas="totalPaginas"
			:totalItens="totalItens"
			@resetar-page="resetarPage"
		></paginacao-tabela>

		<div v-if="isEditar">
			<modal-confirmacao
				id="excluir-documento-listagem-documento"
				:exibir="exibirModalExclusaoDocumentoAnexo"
				:mensagem="getMensagemModalConfirmacaoExclusao()"
				@fechar-modal="abrirOuFecharModalExclusaoDocumentoAnexo"
				@confirmar="excluirDocumentoAnexo"
				:subtituloModal="'Deseja realmente excluir'"
				:tituloModal="getTituloModalConfirmacaoExclusao()"
			></modal-confirmacao>

			<formulario-motivo-cancelamento
				id="cancelar-documento-juntado"
				v-if="exibirModalInformarMotivoCancelamento"
				:exibir="exibirModalInformarMotivoCancelamento"
				titulo="Informe o Motivo do Cancelamento"
				@fechar-modal="abrirModalInformarMotivoCancelamento"
				:titulo-documento="documentoSelecionado.titulo"
				:id-documento="documentoSelecionado.id"
				@confirmar="cancelarDocumento"
			></formulario-motivo-cancelamento>

			<modal-confirmacao
				id="juntar-documento-listagem"
				:tituloModal="getMensagemModalConfirmacaoJuntado()"
				:subtituloModal="mensagemModalJuntarDocumento"
				:exibir="exibirModalJuntarDocumento"
				:mensagem="'Deseja realmente juntar?'"
				:label-acao-confirmar="'CONFIRMAR'"
				:label-acao-cancelar="'CANCELAR'"
				@fechar-modal="cancelarAcaoJuntarDocumentoProcessoDigital"
				@confirmar="juntarDocumentoProcessoDigital"
			></modal-confirmacao>

			<modal-confirmacao
				id="enviar-documento-pncp-listagem"
				:exibir="exibirModalEnviarDocumentoPncp"
				mensagem="Deseja enviar o documento ao PNCP?"
				@fechar-modal="abrirOuFecharModalEnviarPncp"
				@confirmar="enviarPncp"
				:tituloModal="getTituloModalConfirmacaoPncp()"
			></modal-confirmacao>

			<modal-confirmacao
				id="excluir-documento-pncp-listagem"
				:exibir="exibirModalExcluirDocumentoPncp"
				mensagem="Essa ação remove o documento no PNCP e não poderá ser desfeita."
				subtituloModal="Deseja continuar?"
				@fechar-modal="abrirOuFecharModalExcluirPncp"
				@confirmar="excluirPncp"
				:tituloModal="getTituloModalExcluirPncp()"
			></modal-confirmacao>
		</div>
	</div>
</template>
<script>
import tabela from '@/views/mixins/tabela'
import { TabelaColunaUtils } from '@/core/utils'
import ListagemDocumentosBtnMaisAcoes from '@/views/components/documentos/controle-documentos/botoes/ListagemDocumentosBtnMaisAcoes.vue'
import BotaoTooltipDatatable from '@/views/components/BotaoTooltipDatatable.vue'
import TextoTooltip from '@/views/components/TextoTooltip.vue'
import documentoMixin from '@/views/mixins/documento'
import signatario from '@/views/mixins/signatarios/signatario'
import {
	actionTypes,
	mutationTypes,
	opcaoSimNao,
	situacaoSignatarioDocumentoProcessoDigital,
	situacoes,
	situacoesEdocs,
} from '@/core/constants'
import { cloneDeep } from 'lodash'
import PaginacaoTabela from '@/views/components/PaginacaoTabela.vue'
import FormularioMotivoCancelamento from '@/views/components/documentos/motivo-cancelamento/FormularioMotivoCancelamento.vue'
import formatDataFilter from '@/views/filters/format-data-filter'
import TooltipInformativo from '@/views/components/TooltipInformativo.vue'

export default {
	name: 'listagem-documentos',
	components: {
		TooltipInformativo,
		FormularioMotivoCancelamento,
		PaginacaoTabela,
		TextoTooltip,
		BotaoTooltipDatatable,
		ListagemDocumentosBtnMaisAcoes,
	},
	mixins: [tabela, documentoMixin, signatario],
	props: {
		documentos: {
			type: Array,
			default: () => [],
		},
		tipoDocumento: {
			type: Object,
			required: true,
		},
		totalItens: {
			type: Number,
			default: 0,
		},
		atualizaListagem: {
			type: Number,
			default: 0,
		},
		colunaTituloHabilitada: {
			type: Boolean,
			default: () => true,
		},
		colunaDataHabilitada: {
			type: Boolean,
			default: () => true,
		},
		colunaConvidadosHabilitada: {
			type: Boolean,
			default: () => true,
		},
		colunaJuntadoHabilitada: {
			type: Boolean,
			default: () => true,
		},
		usuarioPossuiPermissaoParaJuntar: {
			type: Boolean,
			default: () => true,
		},
		colunaAcoesHabilitada: {
			type: Boolean,
			default: () => true,
		},
		exibirAcoesProcessoDigital: {
			type: Boolean,
			default: true,
		},
		permissaoRedigir: { type: String, default: '' },
		permissaoJuntar: { type: String, default: '' },
		permissaoConcluirEdicaoDocumento: { type: String, default: '' },
		permissaoAssinarEletronicamente: { type: String, default: '' },
		permissaoAssinarDigitalmente: { type: String, default: '' },
		permissaoExcluirDocumento: { type: String, default: '' },
		permissaoBaixarDocumento: { type: String, default: '' },
		permissaoConvidarSignatarios: { type: String, default: '' },
		permissaoCancelarDocumento: { type: String, default: '' },
		permissaoDesentranharDocumento: { type: String, default: '' },
		permissaoPncpEnviar: { type: String, default: '' },
		permissaoPncpExcluir: { type: String, default: '' },
		isEditar: {
			type: Boolean,
			default: () => true,
		},
		hasAuthority: {
			type: Boolean,
		},
		exibirAcoesPncp: {
			type: Boolean,
		},
	},
	emits: ['visualizarDocumentoAnexo', 'excluir', 'carregarListagem'],
	data() {
		return {
			colunas: this.montarColunas(),
			paginacaoInterna: this.paginacao,
			isProcessoDigitalAtivo: this.$store.state.parametrosSistema.isProcessoDigitalAtivo,
			isIntegracaoEdocsAtivo: this.$store.state.parametrosSistema.isIntegracaoEdocsAtivo,
			documentoSelecionado: null,
			exibirModalExclusaoDocumentoAnexo: false,
			mensagemModalJuntarDocumento: null,
			exibirModalJuntarDocumento: false,
			exibirModalInformarMotivoCancelamento: false,
			exibirModalEnviarDocumentoPncp: false,
			exibirModalExcluirDocumentoPncp: false,
			colorsSituacaoEDocs: {
				[situacoesEdocs.AGUARDANDO.value]: 'orange',
				[situacoesEdocs.SINCRONIZADO.value]: 'green',
			},
			iconesSituacaoEDocs: {
				[situacoesEdocs.AGUARDANDO.value]: 'mdi-alert-circle',
				[situacoesEdocs.SINCRONIZADO.value]: 'mdi-check-circle',
			},
			mensagensSituacaoEDocs: {
				[situacoesEdocs.AGUARDANDO.value]: 'Aguardando processamento para envio ao E-docs',
				[situacoesEdocs.SINCRONIZADO.value]: 'Enviado ao E-docs',
			},
		}
	},
	computed: {
		atualizarListagem() {
			return this.atualizaListagem
		},
		hasItens() {
			return this.totalItens > 0
		},
		temPermissao() {
			return this.hasAuthority
		},
		documentosSelecionados: {
			get() {
				return this.$store.state.modalAssinaturaFlowbee.documentosSelecionados
			},
			set(item) {
				this.$store.commit(mutationTypes.COMUM.MODAL_ASSINATURA_FLOWBEE.SET_DOCUMENTOS, item)
			},
		},
		todosItensEstaoSelecionados() {
			return this.quantidadeDocumentosSelecionaveis === this.quantidadeDocumentosSelecionadosPaginaAtual
		},
		quantidadeDocumentosSelecionadosPaginaAtual() {
			return this.documentosSelecionados.filter((documentoSelecionado) => {
				if (this.documentos.findIndex((documento) => documento.id === documentoSelecionado.id) !== -1) {
					return documentoSelecionado
				}
			}).length
		},
		quantidadeDocumentosSelecionaveis() {
			return this.documentos.filter((documento) => this.itemPodeSelecionar(documento)).length
		},
	},
	watch: {
		async documentoSelecionado(novoDocumento) {
			if (novoDocumento) {
				novoDocumento.tipo = this.tipoDocumento
				novoDocumento.idEntidade = novoDocumento.id
			}
		},
	},
	methods: {
		formatDataFilter,
		montarColunas() {
			let colunas = []

			if (this.colunaTituloHabilitada) {
				colunas.push(new TabelaColunaUtils('', 'tipoRegistro', '2%'))
				colunas.push(new TabelaColunaUtils('Título', 'titulo', '40%'))
			}
			if (this.colunaDataHabilitada) colunas.push(new TabelaColunaUtils('Data cadastro', 'dataCadastro', '15%'))

			if (this.colunaConvidadosHabilitada) colunas.push(new TabelaColunaUtils('Convidados', 'signatarios', '15%'))

			if (
				this.colunaJuntadoHabilitada &&
				this.$store.state.parametrosSistema.isProcessoDigitalAtivo === true &&
				this.usuarioPossuiPermissaoParaJuntar === true
			)
				colunas.push(new TabelaColunaUtils('Juntado', 'situacao', '10%'))

			if (this.colunaAcoesHabilitada && this.isEditar)
				colunas.push(new TabelaColunaUtils('Ações', 'acoes', '12%', 'end'))

			return colunas
		},
		deveExibirBotaoJuntar(item) {
			return (
				this.exibirAcoesProcessoDigital &&
				item.podeJuntar &&
				this.usuarioPossuiPermissaoParaJuntar &&
				this.temPermissao &&
				this.isProcessoDigitalAtivo
			)
		},
		tratarEventoVisualizarSignatarios(idDocumento) {
			this.$emit('visualizar-signatarios', idDocumento)
		},
		ehDocumentoProcessoDigital(item) {
			return item.situacao && item.situacao !== situacoes.DOCUMENTO.CRIADO.value && this.isProcessoDigitalAtivo
		},
		getOrdemSituacao(situacao) {
			if (situacao === situacaoSignatarioDocumentoProcessoDigital.ASSINADO.value) {
				return situacaoSignatarioDocumentoProcessoDigital.ASSINADO.ordem
			}

			if (situacao === situacaoSignatarioDocumentoProcessoDigital.EXPIRADO.value) {
				return situacaoSignatarioDocumentoProcessoDigital.EXPIRADO.ordem
			}

			if (situacao === situacaoSignatarioDocumentoProcessoDigital.ENVIADO.value) {
				return situacaoSignatarioDocumentoProcessoDigital.ENVIADO.ordem
			}

			if (situacao === situacaoSignatarioDocumentoProcessoDigital.EXCLUIDO.value) {
				return situacaoSignatarioDocumentoProcessoDigital.EXCLUIDO.ordem
			}
		},
		getListaSignatarios(documento) {
			let documentoCopia = cloneDeep(documento)
			return documentoCopia.signatarios.itens
				.sort((item1, item2) => {
					if (this.getOrdemSituacao(item1.situacao) > this.getOrdemSituacao(item2.situacao)) return -1

					if (this.getOrdemSituacao(item1.situacao) < this.getOrdemSituacao(item2.situacao)) return 1

					return 0
				})
				.slice(0, 5)
		},
		getQuantidadeAssinadosDocumento(documento) {
			if (documento.signatarios) {
				return `${documento.signatarios.quantidadeSignatariosAssinaram} / ${documento.signatarios.quantidadeSignatariosConvidados}`
			}
		},
		ehListaSignatariosVazia(documento) {
			return !documento.signatarios || documento.signatarios.itens.length === 0
		},
		tratarEventoConvidarSignatarios(documento) {
			this.$emit('convidar-signatarios', { idAssociacao: documento.id, titulo: documento.titulo })
		},
		tratarEventoDesentranhar(documento) {
			if (this.isDocumentoEnviadoEdocs(documento)) {
				return
			}

			if (this.possuiTermoDesentranhamento(documento)) {
				this.$emit('continuar-elaboracao-termo-desentranhamento', documento)
				return
			}
			this.$emit('criar-termo-desentranhamento', documento)
		},
		async visualizarDocumentoAnexo(documentoOuAnexo) {
			if (this.estaDesatualizadoOuJuntadoDesatualizado(documentoOuAnexo)) return

			if (this.isDocumento(documentoOuAnexo) && this.documentoEstaCriado(documentoOuAnexo)) {
				const { urlVisualizar } = await this.$store.dispatch(
					actionTypes.DOCUMENTOS.EDITOR_TEXTO.VISUALIZA_GENERICO,
					this.montarObjDocumento(documentoOuAnexo),
				)
				this.redirecionarPara(urlVisualizar)

				return
			}

			await this.baixarDocumentoAnexo(documentoOuAnexo)
		},
		async redigirDocumento(documento) {
			if (this.isDocumento(documento)) {
				const { urlEdicao } = await this.$store.dispatch(
					actionTypes.DOCUMENTOS.EDITOR_TEXTO.REDIGIR_GENERICO,
					this.montarObjDocumento(documento),
				)
				this.redirecionarPara(urlEdicao)
			}
		},
		async baixarDocumentoAnexo(documentoOuAnexo) {
			await this.$store.dispatch(
				actionTypes.DOCUMENTOS.BAIXAR_DOCUMENTO_GENERICO,
				this.montarObjDocumento(documentoOuAnexo),
			)
		},
		async excluirDocumentoAnexo() {
			await this.$store.dispatch(actionTypes.DOCUMENTOS.EXCLUIR_DOCUMENTO_GENERICO, this.montarObjDocumento())

			this.corrigirPaginacaoQuandoContemApenasUmDocumento()
			await this.$emit('carregar-listagem')
			this.exibeMensagemSucessoExclusaoDocumento()
			this.abrirOuFecharModalExclusaoDocumentoAnexo(null)
		},
		async juntarDocumentoProcessoDigital() {
			const payload = {
				idEntidadePai: this.documentoSelecionado.idEntidadePai,
				idsDocumentos: [this.documentoSelecionado.id],
				tipo: this.tipoDocumento,
			}

			if (this.documentoSelecionado.ehTermoDesentranhamento === opcaoSimNao.SIM.value) {
				const payload = { documento: this.documentoSelecionado.id, tipo: this.tipoDocumento }
				await this.$store.dispatch(
					actionTypes.DOCUMENTOS.PROCESSO_DIGITAL.DESENTRANHAMENTO.JUNTAR_TERMO,
					payload,
				)
			} else {
				await this.$store.dispatch(actionTypes.DOCUMENTOS.PROCESSO_DIGITAL.JUNTAR_LOTE_DOCUMENTOS, payload)
			}
			this.documentoSelecionado.situacao = situacoes.DOCUMENTO.JUNTADO.value
			this.documentoSelecionado = null
			this.documentosSelecionados = []
			this.cancelarAcaoJuntarDocumentoProcessoDigital()
			this.mostrarNotificacaoSucesso('Documento foi juntado ao processo digital.')
			await this.$emit('carregar-listagem')
		},
		async concluirEdicaoDocumento(documento) {
			await this.$store.dispatch(actionTypes.DOCUMENTOS.EDITOR_TEXTO.CONCLUIR_DOCUMENTO_GENERICO, {
				idDocumento: documento.id,
				tipo: this.tipoDocumento,
			})
			this.mostrarNotificacaoSucesso('Edição do documento concluída com sucesso')
			await this.$emit('carregar-listagem')
		},
		onSelectAll(value) {
			if (value && !this.isSelecionouTodosItensDaListaDeDocumentosAta) {
				this.selecionarTodosItensListagem()
			} else {
				// this.$store.commit(mutationTypes.ATA.DOCUMENTOS_ATA.CHECKBOX.DESFAZER_SELECAO_ITENS_LISTAGEM)
			}
		},
		abrirModalSelecionarTipoAssinatura(documento) {
			this.$emit('abrir-modal-selecionar-tipo-assinatura', documento)
		},
		abrirOuFecharModalExclusaoDocumentoAnexo(documento) {
			if (this.isDocumentoEnviadoEdocs(documento)) {
				return
			}

			this.documentoSelecionado = documento
			this.exibirModalExclusaoDocumentoAnexo = !this.exibirModalExclusaoDocumentoAnexo
		},
		abrirOuFecharModalJuntarDocumento(documento) {
			this.documentoSelecionado = documento
			this.mensagemModalJuntarDocumento = documento.titulo
			this.exibirModalJuntarDocumento = !this.exibirModalJuntarDocumento
		},
		abrirOuFecharModalEnviarPncp(documento) {
			this.documentoSelecionado = documento
			this.exibirModalEnviarDocumentoPncp = !this.exibirModalEnviarDocumentoPncp
		},
		abrirOuFecharModalExcluirPncp(documento) {
			this.documentoSelecionado = documento
			this.exibirModalExcluirDocumentoPncp = !this.exibirModalExcluirDocumentoPncp
		},
		getTituloModalConfirmacaoPncp() {
			return `Enviar ${this.getTextTipoDocumentoSelecionado()}`
		},
		getTituloModalExcluirPncp() {
			return `Excluir ${this.getTextTipoDocumentoSelecionado()}`
		},
		async enviarPncp() {
			const payload = {
				idEntidadePai: this.documentoSelecionado.idEntidadePai,
				idsDocumentos: [this.documentoSelecionado.id],
				tipo: this.tipoDocumento,
			}

			await this.$store.dispatch(actionTypes.DOCUMENTOS.PNCP.ENVIAR_DOCUMENTO_GENERICO, payload)

			this.mostrarNotificacaoSucesso('Documento enviado ao PNCP com sucesso!')
			await this.$emit('carregar-listagem')
			this.abrirOuFecharModalEnviarPncp()
		},
		async excluirPncp() {
			const payload = { idDocumento: this.documentoSelecionado.id, tipo: this.tipoDocumento }
			await this.$store.dispatch(actionTypes.DOCUMENTOS.PNCP.EXCLUIR_DOCUMENTO_GENERICO, payload)

			this.mostrarNotificacaoSucesso('Documento removido do PNCP com sucesso!')
			await this.$emit('carregar-listagem')
			this.abrirOuFecharModalExcluirPncp()
		},
		cancelarAcaoJuntarDocumentoProcessoDigital() {
			this.documentoSelecionado = null
			this.exibirModalJuntarDocumento = false
		},
		resetarPage() {
			this.$emit('resetar-page')
		},
		podeExibirAcaoRedigir(item) {
			return (
				this.temPermissao &&
				this.isDocumento(item) &&
				this.documentoEstaCriado(item) &&
				item.jaAssinou === false
			)
		},
		podeExibirAcaoAssinar(item) {
			return (
				this.temPermissao &&
				(this.isAnexoPdf(item) || this.isDocumento(item)) &&
				this.documentoOuAnexoEstaEmRascunho(item) &&
				item.jaAssinou === false &&
				this.isProcessoDigitalAtivo
			)
		},
		estaJuntado(docOuAnexo) {
			if (this.documentoOuAnexoEstaJuntado(docOuAnexo)) return 'Sim'
			if (this.documentoOuAnexoEstaJuntadoDesatualizado(docOuAnexo)) return 'Sim'
			return 'Não'
		},
		getClasseLinhasPorSituacao(documentoOuAnexo) {
			if (this.estaCancelado(documentoOuAnexo)) return 'text-decoration-line-through'
			if (this.estaDesatualizadoOuJuntadoDesatualizado(documentoOuAnexo)) return 'documento-desatualizado'
			return ''
		},
		estaDesatualizadoOuJuntadoDesatualizado(documentoOuAnexo) {
			return (
				this.documentoOuAnexoEstaDesatualizado(documentoOuAnexo) ||
				this.documentoOuAnexoEstaJuntadoDesatualizado(documentoOuAnexo)
			)
		},
		estaCancelado(docOuAnexo) {
			return this.documentoOuAnexoEstaCancelado(docOuAnexo)
		},
		exibeMensagemSucessoExclusaoDocumento() {
			this.mostrarNotificacaoSucesso(
				`${this.getTextTipoDocumentoSelecionado()} da ${this.tipoDocumento.text.toLowerCase()} foi excluído com sucesso!`,
			)
		},
		getTextTipoDocumentoSelecionado() {
			if (this.documentoSelecionado) {
				return this.tipoDocumentoRegistro[this.documentoSelecionado.tipoRegistroDocumento].text
			}

			return ''
		},
		getTituloDocumento(documento) {
			return `${documento.titulo}${
				this.isAnexo(documento.tipoRegistroDocumento)
					? ' ' + '(' + this.getConstantText(documento.extensao, this.extensoesArquivos, 'text') + ')'
					: ''
			}`
		},
		getTituloModalConfirmacaoExclusao() {
			return `Excluir ${this.getTextTipoDocumentoSelecionado()}`
		},
		getMensagemModalConfirmacaoExclusao() {
			return `O ${this.getTextTipoDocumentoSelecionado().toLowerCase()} será excluído e não poderá ser recuperado!`
		},
		getMensagemModalConfirmacaoJuntado() {
			return `Atenção! O ${this.getTextTipoDocumentoSelecionado().toLowerCase()} a seguir será juntado ao processo digital:`
		},
		corrigirPaginacaoQuandoContemApenasUmDocumento() {
			if (this.documentos.length === 1 && this.paginacaoInterna.page > 1) {
				this.paginacaoInterna.page -= 1
			}
		},
		selecionarTodosItensListagem() {
			this.$store.commit(mutationTypes.ATA.DOCUMENTOS_ATA.CHECKBOX.SET_IS_SELECIONOU_TODOS_ITENS)
			this.documentosSelecionados = this.documentos.filter((item) => this.itemPodeSelecionar(item))
		},
		possuiTermoDesentranhamento(documento) {
			return opcaoSimNao.SIM.value === documento.possuiTermoDesentranhamento
		},

		abrirModalInformarMotivoCancelamento(documento) {
			if (this.isDocumentoEnviadoEdocs(documento)) {
				return
			}

			this.documentoSelecionado = documento
			this.exibirModalInformarMotivoCancelamento = !this.exibirModalInformarMotivoCancelamento
		},
		isDocumentoEnviadoEdocs(documento) {
			return (
				documento &&
				(situacoesEdocs.AGUARDANDO.value === documento.situacaoEdocs ||
					situacoesEdocs.SINCRONIZADO.value === documento.situacaoEdocs)
			)
		},
		async cancelarDocumento(payload) {
			this.exibirModalInformarMotivoCancelamento = !this.exibirModalInformarMotivoCancelamento

			await this.$store.dispatch(actionTypes.DOCUMENTOS.CANCELAR_DOCUMENTO_GENERICO, {
				idEntidade: payload.idDocumento,
				motivoCancelamento: payload.motivo,
				tipo: this.tipoDocumento,
			})

			this.mostrarNotificacaoSucesso('Alteração realizada com sucesso')
			await this.$emit('carregar-listagem')
		},
		itemPodeSelecionar(item) {
			return false
			// return (
			// 	this.temProcessoDigital(item) &&
			// 	!this.documentoOuAnexoEstaJuntado(item) &&
			// 	!this.documentoOuAnexoEstaCancelado(item) &&
			// 	!this.possuiTermoDesentranhamento(item)
			// )
		},
		montarObjDocumento(documento = this.documentoSelecionado) {
			if (!documento) throw Error('Selecione um documento para realizar a operação.')

			return {
				idDocumento: documento.id,
				idEntidade: documento.id,
				tipo: this.tipoDocumento,
				documento: documento,
			}
		},
		exibirIconeSituacaoEDocs(item) {
			return this.isIntegracaoEdocsAtivo && item.situacaoEdocs
		},
	},
}
</script>
<style lang="stylus" scoped>
.documento-desatualizado
	color var(--v-grey-darken1)
	text-decoration line-through
</style>
